import React, {forwardRef, useMemo} from 'react'
import PropTypes from 'prop-types'
import {css, Global} from '@emotion/react'
import ReactDatePicker from 'react-datepicker'
import reactDatePickerCss from 'react-datepicker/dist/react-datepicker.css'
import {useIntl} from 'react-intl'
import {Input, useMergeRefs} from '@chakra-ui/react'
import useDateFnsLocale from '../../hooks/use-date-fns-locale'
import {addYears, subYears} from 'date-fns'
import {formatBackendDate, parseBackendDate} from '../../utils/date-utils'

const CustomDatePickerInput = forwardRef(({inputRef, ...props}, ref) => {
    const combinedRef = useMergeRefs(ref, inputRef)
    return <Input ref={combinedRef} {...props} />
})

if (process.env.NODE_ENV !== 'production') {
    CustomDatePickerInput.displayName = 'CustomDatePickerInput'
}

CustomDatePickerInput.propTypes = {
    inputRef: PropTypes.oneOfType([PropTypes.object, PropTypes.func])
}

const DatePicker = forwardRef(
    ({inputProps, placeholder, value, onBlur, onChange, onFocus}, ref) => {
        const {formatDate} = useIntl()

        const {data: dateFnsLocale} = useDateFnsLocale()

        // memoize to prevent creating new date instances on every render
        const selected = useMemo(() => parseBackendDate(value), [value])

        // extract the date format from react-intl using a hack from https://stackoverflow.com/a/52498569
        const dateFormat = useMemo(() => {
            // we need to create a date object at noon in the current timezone to avoid day shifts
            const intlString = formatDate(new Date(1999, 1, 3, 12, 0, 0))

            const dateFormat = intlString
                .replace('03', 'dd')
                .replace('3', 'd')
                .replace('02', 'MM')
                .replace('2', 'M')
                .replace('1999', 'yyyy')

            return dateFormat
        }, [formatDate])

        return (
            <>
                <Global
                    styles={css`
                        ${reactDatePickerCss}

                        .react-datepicker-wrapper {
                            width: 100%;
                        }
                        .react-datepicker__day--disabled {
                            color: #6f6f6f;
                        }
                    `}
                />
                <ReactDatePicker
                    customInput={<CustomDatePickerInput ref={ref} {...inputProps} />}
                    customInputRef="inputRef"
                    dateFormat={dateFormat}
                    locale={dateFnsLocale}
                    placeholderText={placeholder}
                    selected={selected}
                    showYearDropdown
                    yearDropdownItemNumber={100}
                    maxDate={addYears(new Date(), 0)}
                    minDate={subYears(new Date(), 100)}
                    scrollableYearDropdown
                    onBlur={onBlur}
                    onChange={(date) => {
                        const isoLocal = formatBackendDate(date)
                        onChange?.(isoLocal)
                    }}
                    onFocus={onFocus}
                />
            </>
        )
    }
)

if (process.env.NODE_ENV !== 'production') {
    DatePicker.displayName = 'DatePicker'
}

DatePicker.propTypes = {
    inputProps: PropTypes.object,
    placeholder: PropTypes.string,
    value: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onFocus: PropTypes.func
}

export default DatePicker
